import { useCallback, useState } from 'react';
import { useCreatePageModuleMutation, useDeletePageModuleMutation, useMoveModuleToNewRowMutation, useUpdatePageModuleMutation } from '../editCourseApi';
import { useEditMessageMutation } from '../../viewCourse/discussionApi';
import { useUpdateTaskMutation } from '../../tasks/tasksApi';
import { getSaveStrategy } from '../utils/moduleSaveStrategies';
import { extractCreatedModuleId } from '../utils/extractCreatedModuleId';

export function useModuleOperations({ pageId, t }) {
  const [isSaving, setIsSaving] = useState(false);

  const [updatePageModule] = useUpdatePageModuleMutation();
  const [deletePageModule] = useDeletePageModuleMutation();
  const [createPageModule] = useCreatePageModuleMutation();
  const [moveModuleToNewRow] = useMoveModuleToNewRowMutation();

  const [updateTask] = useUpdateTaskMutation();
  const [updateDiscussionContent] = useEditMessageMutation();

  const saveModuleContent = useCallback(
    async (module, data) => {
      setIsSaving(true);
      try {
        const moduleType = module?.type?.name;
        const strategy = getSaveStrategy(moduleType);

        if (module._isNew) {
          const prepared = strategy.prepareCreate({ module, data, pageId });
          const response = await createPageModule(prepared).unwrap();

          const createdModuleId = extractCreatedModuleId(response);

          if (module._position !== undefined && createdModuleId) {
            const pageRowPosition = module._position + 1; // Convert to 1-based
            await moveModuleToNewRow({
              moduleId: createdModuleId,
              pageRowPosition,
              pageId,
            }).unwrap();
          }

          return { createdModuleId };
        }

        await strategy.update({
          module,
          data,
          pageId,
          mutations: {
            updatePageModule,
            updateTask,
            updateDiscussionContent,
          },
        });

        return { createdModuleId: null };
      } finally {
        setIsSaving(false);
      }
    },
    [createPageModule, moveModuleToNewRow, pageId, updateDiscussionContent, updatePageModule, updateTask]
  );

  const saveModuleSettings = useCallback(
    async (module, data) => {
      setIsSaving(true);
      try {
        await updatePageModule({
          moduleId: module.id,
          pageId,
          params: data.params,
        }).unwrap();
      } finally {
        setIsSaving(false);
      }
    },
    [pageId, updatePageModule]
  );

  const deleteModule = useCallback(
    async (module) => {
      setIsSaving(true);
      try {
        await deletePageModule({
          moduleId: module.id,
          pageId,
        }).unwrap();

        return { deleted: true };
      } finally {
        setIsSaving(false);
      }
    },
    [deletePageModule, pageId]
  );

  return {
    isSaving,
    saveModuleContent,
    saveModuleSettings,
    deleteModule,
  };
}
